import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RecommendationTabs, convertStatusFromNumberRecommendation, RecommendationDto } from '@cloudclevr/components';
import { DropdownData } from '@components/ui/Dropdown';
import RecommendationsList from './RecommendationsList';
// Adjust the import path below to the actual export location of RecommendationsList in @cloudclevr/components
//import { RecommendationsList } from '@cloudclevr/components';
import { useCategoryRecommendationsQuery } from '@hooks/roadmaps/useRecommendationMutations';

export interface TabData {
  key: string;
  label: string;
  count: number;
}

export interface CategoryDetailPageProps {
  categoryId: string;
  categoryName: string;
  customerName?: string;
  roadmapId: string;
  riskProfile: {
    level: 'Critical' | 'Moderate' | 'Low' | 'Information';
  };
  accountOwner: {
    name: string;
    email: string;
    avatar?: string;
  };
  onAddRecommendation?: () => void;
  onBackClick?: () => void;
  onViewNotes?: (id: string) => void;
  onDownloadAttachments?: (id: string) => void;
  onDownloadAttachment: (roadmapId: string, recommendationId: string, attachmentId: string) => Promise<string>;
  onViewQuote?: (id: string) => void;
  className?: string;
  onRecommendationDropdownAction?: (action: string, recommendationId: string) => void;
  recommendationDropdownActions?: DropdownData[];
  onSetTabSwitcher?: (tabSwitcher: () => void) => void;
  onSetOpenTabSwitcher?: (tabSwitcher: () => void) => void;
  onSetDeclinedTabSwitcher?: (tabSwitcher: () => void) => void;
  onSetInProgressTabSwitcher?: (tabSwitcher: () => void) => void;
  onSetCompletedTabSwitcher?: (tabSwitcher: () => void) => void;
  onDownloadSuccess?: (fileName: string) => void;
  onDownloadError?: (fileName: string) => void;
}

const CategoryDetailPage: React.FC<CategoryDetailPageProps> = ({
  categoryId,
  categoryName,
  customerName,
  roadmapId,
  riskProfile,
  accountOwner,
  onViewNotes,
  onDownloadAttachments,
  onDownloadAttachment,
  onViewQuote,
  onRecommendationDropdownAction,
  recommendationDropdownActions = [],
  className = '',
  onSetTabSwitcher,
  onSetOpenTabSwitcher,
  onSetDeclinedTabSwitcher,
  onSetInProgressTabSwitcher,
  onSetCompletedTabSwitcher,
  onDownloadSuccess,
  onDownloadError

}) => {
  const navigate = useNavigate();  
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial tab from URL params, default to 'open'
  const initialTab = searchParams.get('tab') || 'open';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Fetch all recommendations for this category
  const { data: allRecommendations = [], isLoading, error } = useCategoryRecommendationsQuery(
      categoryId,
      undefined,      
      ['Category','Costs', 'SubCategory', 'Attachments']
    );  
  const handleTabChange = useCallback((newTab: string) => {
    setActiveTab(newTab);
    
    // Update URL params
    const newSearchParams = new URLSearchParams(searchParams);
    if (newTab === 'open') {
      // Remove tab param if it's the default tab
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', newTab);
    }
    
    // Update URL without causing navigation
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  
  const switchToAcceptedTab = useCallback(() => {
    handleTabChange('accepted');
  }, [handleTabChange]);

  const switchToOpenTab = useCallback(() => {
    handleTabChange('open');
  }, [handleTabChange]);

  const switchToDeclinedTab = useCallback(() => {
    handleTabChange('declined');
  }, [handleTabChange]);

  const switchToInProgressTab = useCallback(() => {
    handleTabChange('inProgress');
  }, [handleTabChange]);

  const switchToCompletedTab = useCallback(() => {
    handleTabChange('completed');
  }, [handleTabChange]);


  useEffect(() => {
    if (onSetTabSwitcher) {
      onSetTabSwitcher(switchToAcceptedTab);
    }
    if (onSetOpenTabSwitcher) {
      onSetOpenTabSwitcher(switchToOpenTab);
    }
    if (onSetDeclinedTabSwitcher) {
      onSetDeclinedTabSwitcher(switchToDeclinedTab);
    }
    if (onSetInProgressTabSwitcher) {
      onSetInProgressTabSwitcher(switchToInProgressTab);
    }
    if (onSetCompletedTabSwitcher) {
      onSetCompletedTabSwitcher(switchToCompletedTab);
    }
  }, [
    onSetTabSwitcher, 
    onSetOpenTabSwitcher, 
    onSetDeclinedTabSwitcher, 
    onSetInProgressTabSwitcher,
    switchToAcceptedTab,
    switchToOpenTab,
    switchToDeclinedTab,
    switchToInProgressTab,
    switchToCompletedTab
  ]);
 // Group recommendations by status
  const groupedRecommendations = useMemo(() => {
    const groups = {
      open: [] as RecommendationDto[],
      accepted: [] as RecommendationDto[],
      inProgress: [] as RecommendationDto[],
      completed: [] as RecommendationDto[],
      declined: [] as RecommendationDto[]
    };

    allRecommendations.forEach(recommendation => {
    // Now recommendation.status is correctly typed as number
    const statusString = convertStatusFromNumberRecommendation(recommendation.status);
    
    switch (statusString) {
      case 'Open':
        groups.open.push(recommendation);
        break;
      case 'Accepted':
        groups.accepted.push(recommendation);
        break;
      case 'InProgress':
        groups.inProgress.push(recommendation);
        break;
      case 'Completed':
        groups.completed.push(recommendation);
        break;
      case 'Declined':
        groups.declined.push(recommendation);
        break;
      default:
        groups.open.push(recommendation);
    }
  });

    return groups;
  }, [allRecommendations]);

  // Create tabs with counts
  const tabs: TabData[] = [
    {
      key: 'open',
      label: 'Open recommendations',
      count: groupedRecommendations.open.length
    },
    {
      key: 'accepted',
      label: 'Accepted',
      count: groupedRecommendations.accepted.length
    },
    {
      key: 'inProgress',
      label: 'In progress',
      count: groupedRecommendations.inProgress.length
    },
    {
      key: 'completed',
      label: 'Completed',
      count: groupedRecommendations.completed.length
    },
    {
      key: 'declined',
      label: 'Declined',
      count: groupedRecommendations.declined.length
    }
  ];

  // Get current recommendations based on active tab
  const getCurrentRecommendations = () => {    
    switch (activeTab) {
      case 'open':
        return groupedRecommendations.open;
      case 'accepted':
        return groupedRecommendations.accepted;
      case 'inProgress':
        return groupedRecommendations.inProgress;
      case 'completed':
        return groupedRecommendations.completed;
      case 'declined':
        return groupedRecommendations.declined;
      default:
        return groupedRecommendations.open;
    }
  };

  const handleBackToRoadmap = () => {
    if (roadmapId) {
      navigate(`/roadmaps/${roadmapId}`);
    } else {
      navigate('/roadmaps');
    }
  };

  useEffect(() => {
  
//   if (allRecommendations.length > 0) {    
//   }
}, [allRecommendations]);

  // Loading state
  if (isLoading) {
    return (
      <div className={`min-h-screen bg-gray-50 ${className}`}>
        <div className="px-3 py-0">
          {/* <CategoryDetailHeader
            categoryId={categoryId}
            roadmapId={roadmapId}
            categoryName={categoryName}
            customerName={customerName}
            riskProfile={riskProfile}
            accountOwner={accountOwner}
            onBackClick={() => console.log('Back clicked')}
          /> */}
        </div>
        <div className="px-3 py-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Loading recommendations...</div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`min-h-screen bg-gray-50 ${className}`}>
        <div className="px-3 py-0">
          {/* <CategoryDetailHeader
            categoryId={categoryId}
            roadmapId={roadmapId}
            categoryName={categoryName}
            customerName={customerName}
            riskProfile={riskProfile}
            accountOwner={accountOwner}
            onBackClick={() => console.log('Back clicked')}
          /> */}
        </div>
        <div className="px-3 py-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-red-500">Error loading recommendations. Please try again.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 ${className}`}>     

      {/* Tabs */}
      <div className="px-3">
        <RecommendationTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </div>

      {/* Recommendations List */}
      <div className="px-3 py-6">       
        <RecommendationsList
          recommendations={getCurrentRecommendations()}
          roadmapId={roadmapId}
          onViewNotes={onViewNotes}
          onDownloadAttachments={onDownloadAttachments}
          //onDownloadAttachment={onDownloadAttachment}
          onViewQuote={onViewQuote}
          onDropdownAction={onRecommendationDropdownAction}
          dropdownActions={recommendationDropdownActions}
          onBackToRoadmap={handleBackToRoadmap}
          currentTab={activeTab}
          onDownloadSuccess={onDownloadSuccess}
          onDownloadError={onDownloadError}
        />
      </div>
    </div>
  );
};
export default CategoryDetailPage;